import { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { AlertCircle, Loader } from 'lucide-react'
import { useAuthStore } from '../services/authStore'
import { authService } from '../services/authService'
import ThemeToggle from '../components/theme/ThemeToggle'
import ApkDownloadGate from '../components/auth/ApkDownloadGate'
import brandLogo from '../assets/branding/savinfy-receipts-logo.png'

export default function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  const { login, isLoading, error, clearError } = useAuthStore()

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  })
  const [validationErrors, setValidationErrors] = useState({})

  const validateForm = () => {
    const errors = {}

    if (!formData.email) {
      errors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email'
    }

    if (!formData.password) {
      errors.password = 'Password is required'
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters'
    }

    return errors
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: '',
      }))
    }
    clearError()
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const errors = validateForm()
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors)
      return
    }

    try {
      await login(formData.email, formData.password)
      navigate('/')
    } catch (_err) {
      // Error is handled by the store.
    }
  }

  useEffect(() => {
    const messageHandler = (event) => {
      if (!event.data || !event.data.token || !event.data.user) return

      const applied = authService.applySessionFromExternalAuth(event.data.token, event.data.user)
      if (!applied) return
      useAuthStore.setState({ user: event.data.user, isAuthenticated: true, isInitializing: false })
      navigate('/')
    }

    window.addEventListener('message', messageHandler)

    return () => {
      window.removeEventListener('message', messageHandler)
    }
  }, [navigate])

  const apiOrigin = import.meta.env.VITE_API_URL || `${window.location.protocol}//${window.location.host}/api`
  const socialUrlBase = apiOrigin.replace(/\/+$/, '')

  const handleSocialLogin = (provider) => {
    const params = new URLSearchParams(location.search)
    const redirect = params.get('redirect') || '/'

    localStorage.setItem('social_login_redirect', redirect)

    const popup = window.open(
      `${socialUrlBase}/auth/social/${provider}/redirect`,
      'SocialLogin',
      'width=600,height=700',
    )

    if (!popup) {
      alert('Please allow popups for this site.')
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-surface-elevated text-foreground flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="mb-4 flex justify-center">
            <ThemeToggle />
          </div>
          <img src={brandLogo} alt="Savinfy Receipts" className="mx-auto mb-4 h-14 w-auto" />
          <h1 data-testid="brand-wordmark" className="text-4xl font-bold text-foreground mb-2">
            Savinfy Receipts
          </h1>
          <p className="text-muted-foreground">Capture. Parse. Organize.</p>
        </div>

        <div className="bg-surface border border-outline rounded-lg shadow-lg p-8 space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-foreground">Sign in</h2>
            <p className="text-muted-foreground text-sm mt-1">Use your email and password to continue.</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3">
              <AlertCircle className="text-red-600 flex-shrink-0" size={20} />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                Email Address
              </label>
              <input
                id="email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition ${
                  validationErrors.email ? 'border-red-500' : 'border-outline'
                }`}
                disabled={isLoading}
              />
              {validationErrors.email && (
                <p className="text-red-600 text-sm mt-1">{validationErrors.email}</p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="********"
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition ${
                  validationErrors.password ? 'border-red-500' : 'border-outline'
                }`}
                disabled={isLoading}
              />
              {validationErrors.password && (
                <p className="text-red-600 text-sm mt-1">{validationErrors.password}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white font-semibold py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader size={20} className="animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-outline"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-surface text-muted-foreground">or</span>
            </div>
          </div>

          <p className="text-center text-muted-foreground text-sm">
            <Link to="/forgot-password" className="text-blue-600 hover:underline font-semibold">
              Forgot password?
            </Link>
          </p>

          <div className="mt-4">
            <p className="text-center text-muted-foreground text-sm mb-2">Continue with</p>
            <div className="grid grid-cols-3 gap-2">
              {['google', 'microsoft', 'facebook'].map((provider) => (
                <button
                  key={provider}
                  type="button"
                  onClick={() => handleSocialLogin(provider)}
                  className="bg-surface-elevated hover:bg-gray-200 text-foreground py-2 rounded"
                >
                  {provider.charAt(0).toUpperCase() + provider.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <p className="text-center text-muted-foreground text-sm mt-4">
            Don't have an account?{' '}
            <Link to="/register" className="text-blue-600 hover:underline font-semibold">
              Sign Up
            </Link>
          </p>
        </div>

        <ApkDownloadGate />
      </div>
    </div>
  )
}
